<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import InputText from 'primevue/inputtext'
import ProgressSpinner from 'primevue/progressspinner'
import SubjectCheckbox from './SubjectCheckbox.vue'
import { usePresentLevelsWizardStore } from './present_levels_wizard.store'
import { WIZARD_EVENT_NAMES } from './event_names'
import {
  getAreasOfNeed,
  getEnrolledGradesSet,
  getPresentLevelStudentStatement,
  postPresentLevelStudent
} from '@/presentlevels/api_v1.js'
import { useRedirectIfUnauthorized } from '@/authentication/useRedirectIfUnauthorized'
import { useUserStore } from '@/authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { betaOptIn } from '@/util/beta_opt_in'

type AreaOfNeed = { label: string; value: string; dateAdded?: Date }
type GradeOption = { name: string; val: string }
type StudentRecord = {
  uuid?: string
  user_uuid: string
  student_name: string
  enrolled_grade_level: string
  selected_subjects: string[]
}
type StudentResponse = { student: StudentRecord }

const NEW_WIZARD_ROUTE = 'PresentLevelsNewWizard'
const EXISTING_WIZARD_ROUTE = 'PresentLevelsExistingWizard'
const SUBJECT_RESULTS_ROUTE = 'PresentlevelsSubjectResults'
// effect-statement profiles develop Behavior through its effect statements instead
const SUBJECT_BLOCKED_FOR_EFFECTS = 'Behavior'
// the first column holds the academic areas, which is why it breaks after the seventh
const FIRST_COLUMN_SIZE = 7
const NO_GRADE = ''
const HTML_TAGS = /(<([^>]+)>)/gi

const props = withDefaults(defineProps<{ uuid?: string | null }>(), { uuid: null })

const route = useRoute()
const router = useRouter()
const analytics = getAnalyticsTracker()
const wizardStore = usePresentLevelsWizardStore()
const userStore = useUserStore()
const { selVisibility, effectsVisibility } = storeToRefs(useVisibilityStore())
const { redirectIfUnauthorized } = useRedirectIfUnauthorized()

const studentName = ref('')
const selectedGrade = ref(NO_GRADE)
const selectedSubjects = ref<string[]>([])
const isDeveloping = ref(false)

const enrolledGrades: GradeOption[] = getEnrolledGradesSet()

const areasOfNeed = computed<AreaOfNeed[]>(() => {
  const areas: AreaOfNeed[] = getAreasOfNeed({ selBlocked: !selVisibility.value, iodSubject: false })
  if (!effectsVisibility.value) {
    return areas
  }
  return areas.filter((area) => area.value !== SUBJECT_BLOCKED_FOR_EFFECTS)
})
const areaColumns = computed(() => [
  areasOfNeed.value.slice(0, FIRST_COLUMN_SIZE),
  areasOfNeed.value.slice(FIRST_COLUMN_SIZE)
])

const isGradeChosen = computed(() => selectedGrade.value !== NO_GRADE)
const isSubmittable = computed(
  () => studentName.value !== '' && isGradeChosen.value && selectedSubjects.value.length > 0
)

function checkboxIdFor(label: string) {
  return label.toLowerCase().trim().split(' ').join('-')
}

function stripHtmlTags(text: string) {
  return text.replace(HTML_TAGS, '')
}

function subjectsInWizardOrder() {
  return areasOfNeed.value.filter((area) => selectedSubjects.value.includes(area.value)).map((area) => area.value)
}

function withBetaOptIn() {
  return betaOptIn(route.query)
}

function showStudent(student: StudentRecord, uuid: string) {
  studentName.value = student.student_name
  selectedGrade.value = student.enrolled_grade_level
  selectedSubjects.value = student.selected_subjects
  wizardStore.lastUpdate = new Date()
  wizardStore.setBreadcrumbsInfo({ uuid, availableStages: student.selected_subjects })
  wizardStore.setStudentInfo({
    studentName: student.student_name,
    enrolledGrade: student.enrolled_grade_level,
    areasOfNeed: student.selected_subjects
  })
}

async function loadStudent(uuid: string) {
  const newWizard = { name: NEW_WIZARD_ROUTE, query: withBetaOptIn() }
  try {
    const { student }: StudentResponse = await getPresentLevelStudentStatement(uuid)
    await redirectIfUnauthorized(student.user_uuid, newWizard)
    showStudent(student, uuid)
  } catch (error) {
    router.replace(newWizard)
    console.error(error)
  }
}

async function goToFirstSubject(student: StudentRecord & { uuid: string }) {
  const results = {
    name: SUBJECT_RESULTS_ROUTE,
    params: { uuid: student.uuid, subject: student.selected_subjects[0] },
    query: withBetaOptIn()
  }
  // the wizard's own URL takes the new uuid first, so Back returns to this student's info
  if (route.params.uuid !== student.uuid) {
    await router.replace({ name: EXISTING_WIZARD_ROUTE, params: { uuid: student.uuid }, query: withBetaOptIn() })
  }
  await router.push(results)
}

async function developPresentLevels() {
  const clickedDevelop = {
    enrolled_grade: selectedGrade.value,
    areas_of_need: selectedSubjects.value
  }
  if (wizardStore.blockIfDevelopingIsNotAllowed()) {
    analytics.sendEvent(WIZARD_EVENT_NAMES.CLICKED_DEVELOP, clickedDevelop)
    return
  }
  selectedSubjects.value = subjectsInWizardOrder()
  wizardStore.setStudentInfo({
    studentName: studentName.value,
    enrolledGrade: selectedGrade.value,
    areasOfNeed: selectedSubjects.value
  })
  // Sent after the store holds the prompts, which the shell's plw_enrolled_grade and plw_areas_of_need read.
  analytics.sendEvent(WIZARD_EVENT_NAMES.CLICKED_DEVELOP, clickedDevelop)
  isDeveloping.value = true
  try {
    const { student }: StudentResponse = await postPresentLevelStudent({
      uuid: props.uuid,
      name: stripHtmlTags(studentName.value),
      pronoun: '',
      enrolledGrade: selectedGrade.value,
      subjects: selectedSubjects.value,
      userUuid: userStore.userId
    })
    if (!student.uuid) {
      return
    }
    wizardStore.lastUpdate = new Date()
    await goToFirstSubject({ ...student, uuid: student.uuid })
  } catch (error) {
    console.error(error)
  } finally {
    isDeveloping.value = false
  }
}

if (props.uuid) {
  loadStudent(props.uuid)
} else {
  wizardStore.resetStudentInfo()
}
</script>

<template>
  <div class="rounded-[10px] bg-blue-200 p-4 shadow-md">
    <form id="presentlevelsForm" class="px-2 pb-2 pt-4" @submit.prevent>
      <div class="mt-5">
        <label for="studentNameInput" class="block text-display-sm font-regular italic sm:text-2xl sm:leading-[3.75rem]">
          <span>I'm developing present levels for </span>
          <InputText
            id="studentNameInput"
            v-model="studentName"
            class="fs-block mt-2 block w-full max-w-[500px] px-2.5 py-2.5 text-display-sm not-italic !text-gray-800 sm:h-[62px] sm:text-2xl"
            placeholder="my student's name"
          />
        </label>
      </div>

      <div class="mt-5">
        <label for="gradeSelectInput" class="block text-display-sm font-regular italic sm:text-2xl sm:leading-[3.75rem]">
          <span>who is enrolled in</span>
          <select
            id="gradeSelectInput"
            v-model="selectedGrade"
            class="mt-2 block w-full max-w-[500px] rounded-xs border border-gray-600 bg-white px-2.5 text-display-sm not-italic focus:border-blue-400 sm:h-[62px] sm:text-2xl"
            :class="{ 'text-gray-700': !isGradeChosen }"
          >
            <option :value="NO_GRADE" disabled>select grade level</option>
            <option v-for="grade in enrolledGrades" :key="grade.val" :value="grade.val">{{ grade.name }}</option>
          </select>
        </label>
      </div>

      <fieldset class="mt-5">
        <legend class="mb-0 text-display-sm font-regular italic sm:text-2xl sm:leading-[3.75rem]">
          and has areas of strengths/needs in
        </legend>
        <div class="grid gap-x-[30px] sm:grid-cols-2">
          <div v-for="(column, index) in areaColumns" :key="index">
            <SubjectCheckbox
              v-for="area in column"
              :key="area.value"
              v-model="selectedSubjects"
              :label="area.label"
              :value="area.value"
              :date-added="area.dateAdded"
              :input-id="checkboxIdFor(area.label)"
            />
          </div>
        </div>
      </fieldset>

      <div class="mt-5 text-center">
        <button
          v-if="isSubmittable"
          id="plActionButton"
          type="button"
          :disabled="isDeveloping"
          class="mt-5 inline-flex max-w-full items-center gap-2 rounded-xs border border-transparent bg-blue-400 px-6 py-4 text-display-sm text-white hover:bg-blue-500 focus-visible:outline-offset-2 disabled:opacity-65"
          @click="developPresentLevels"
        >
          <ProgressSpinner v-if="isDeveloping" stroke-width="8" class="h-6 w-6 stroke-white" aria-hidden="true" />
          Develop present levels
        </button>
      </div>
    </form>
  </div>
</template>
